import React, { type CSSProperties } from "react";
import { Button } from "antd";
import { ButtonProps } from "antd/lib/button/button";

import { theme } from "../theme";
import "./ToggleButton.scss";

type ToggleButtonProps = ButtonProps & {
  isOn: boolean;
  onToggle?: (
    nextState: boolean,
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => void;
  onText?: React.ReactNode;
  offText?: React.ReactNode;
  iconColors?: {
    on?: string;
    off?: string;
  };
};

export const ToggleButton: React.FC<ToggleButtonProps> = ({
  isOn,
  onToggle,
  onText,
  offText,
  icon,
  iconColors,
  className,
  children,
  onClick,
  disabled,
  type,
  ...rest
}) => {
  const buttonLabel = (isOn ? onText : offText) ?? children;

  const iconColor = isOn ? iconColors?.on ?? theme.primary : iconColors?.off;

  let coloredIcon = icon;
  if (icon && React.isValidElement(icon)) {
    const iconElement = icon as React.ReactElement<{
      style?: CSSProperties;
    }>;
    coloredIcon = React.cloneElement(iconElement, {
      style: {
        ...(iconElement.props.style || {}),
        color:
          iconColor !== undefined
            ? iconColor
            : iconElement.props.style?.color,
      },
    });
  }

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    onClick?.(event);
    if (event.defaultPrevented || disabled) {
      return;
    }
    onToggle?.(!isOn, event);
  };

  const buttonClassName = ["ToggleButton", isOn ? "is-on" : "is-off", className]
    .filter(Boolean)
    .join(" ");

  return (
    <Button
      {...rest}
      type={type ?? "default"}
      className={buttonClassName}
      onClick={handleClick}
      icon={coloredIcon}
      aria-pressed={isOn}
      disabled={disabled}
    >
      {buttonLabel}
    </Button>
  );
};
